import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { challengeService, squadService, friendshipService } from '../../../services/socialService';

const CreateChallengeModal = ({ isOpen, onClose, onChallengeCreated, userId }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [squads, setSquads] = useState([]);
  const [friends, setFriends] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    challengeType: 'steps',
    targetValue: '',
    unit: 'steps',
    durationDays: 7,
    icon: 'Target',
    color: 'primary',
    invitationType: 'squad', // 'squad', 'username', 'email'
    selectedSquadId: null,
    selectedUserIds: [],
    inviteEmail: '',
    inviteUsername: ''
  });

  useEffect(() => {
    if (isOpen && userId) {
      fetchSquadsAndFriends();
    }
  }, [isOpen, userId]);

  const fetchSquadsAndFriends = async () => {
    try {
      const [squadsData, friendsData] = await Promise.all([
        squadService?.getUserSquads(userId),
        friendshipService?.getFriends(userId)
      ]);
      setSquads(squadsData || []);
      setFriends(friendsData || []);
    } catch (error) {
      console.error('Error fetching squads and friends:', error);
    }
  };

  const handleSearchUsers = async (term) => {
    setSearchTerm(term);
    if (!term?.trim() || term?.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const results = await friendshipService?.searchUsers(term, userId);
      setSearchResults(results || []);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleCreateChallenge = async () => {
    if (!formData?.title?.trim() || !formData?.targetValue) return;

    setLoading(true);
    try {
      // Create the challenge
      const challengeData = {
        title: formData?.title,
        description: formData?.description,
        challengeType: formData?.challengeType,
        targetValue: parseFloat(formData?.targetValue),
        unit: formData?.unit,
        durationDays: formData?.durationDays,
        icon: formData?.icon,
        color: formData?.color,
        invitationType: formData?.invitationType,
        squadId: formData?.selectedSquadId,
        userIds: formData?.selectedUserIds,
        inviteEmail: formData?.inviteEmail,
        inviteUsername: formData?.inviteUsername
      };

      await challengeService?.createCustomChallenge(userId, challengeData);
      onChallengeCreated();
      resetForm();
    } catch (error) {
      console.error('Error creating challenge:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      challengeType: 'steps',
      targetValue: '',
      unit: 'steps',
      durationDays: 7,
      icon: 'Target',
      color: 'primary',
      invitationType: 'squad',
      selectedSquadId: null,
      selectedUserIds: [],
      inviteEmail: '',
      inviteUsername: ''
    });
    setStep(1);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const challengeTypes = [
    { value: 'steps', label: 'Pasos', unit: 'pasos', icon: 'Footprints' },
    { value: 'hydration', label: 'Hidratación', unit: 'vasos', icon: 'Droplets' },
    { value: 'sleep', label: 'Sueño', unit: 'horas', icon: 'Moon' },
    { value: 'calories', label: 'Calorías', unit: 'kcal', icon: 'Flame' },
    { value: 'workouts', label: 'Entrenamientos', unit: 'sesiones', icon: 'Dumbbell' }
  ];

  const colorOptions = [
    { value: 'primary', label: 'Azul', class: 'bg-primary' },
    { value: 'success', label: 'Verde', class: 'bg-success' },
    { value: 'warning', label: 'Naranja', class: 'bg-warning' },
    { value: 'accent', label: 'Morado', class: 'bg-accent' }
  ];

  const toggleUserSelection = (userId) => {
    setFormData(prev => ({
      ...prev,
      selectedUserIds: prev?.selectedUserIds?.includes(userId)
        ? prev?.selectedUserIds?.filter(id => id !== userId)
        : [...prev?.selectedUserIds, userId]
    }));
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={handleClose}
      />
      
      <div className="relative w-full max-w-3xl bg-card rounded-xl shadow-wellness-lg animate-scale-in elevation-3 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/40">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Crear Reto</h2>
            <p className="text-sm text-muted-foreground">
              {step === 1 ? 'Define el reto' : 'Invita participantes'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Step 1: Challenge Details */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Challenge Title */}
              <Input
                label="Título del Reto"
                value={formData?.title}
                onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                placeholder="Ej: Reto de 10,000 pasos diarios"
                required
              />

              {/* Challenge Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  placeholder="Describe el objetivo del reto..."
                  rows={3}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                />
              </div>

              {/* Challenge Type */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Tipo de Reto
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {challengeTypes?.map((type) => (
                    <button
                      key={type?.value}
                      onClick={() => setFormData({ 
                        ...formData, 
                        challengeType: type?.value,
                        unit: type?.unit,
                        icon: type?.icon
                      })}
                      className={`p-4 flex flex-col items-center space-y-2 border-2 rounded-xl transition-all ${
                        formData?.challengeType === type?.value
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <Icon name={type?.icon} size={24} className="text-foreground" />
                      <span className="text-sm font-medium text-foreground">{type?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Target Value & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Meta"
                  type="number"
                  value={formData?.targetValue}
                  onChange={(e) => setFormData({ ...formData, targetValue: e?.target?.value })}
                  placeholder="Ej: 10000"
                  required
                />
                <Input
                  label="Duración (días)"
                  type="number"
                  value={formData?.durationDays}
                  onChange={(e) => setFormData({ ...formData, durationDays: parseInt(e?.target?.value) || 7 })}
                  placeholder="7"
                  required
                />
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  Color del Reto
                </label>
                <div className="flex space-x-3">
                  {colorOptions?.map((color) => (
                    <button
                      key={color?.value}
                      onClick={() => setFormData({ ...formData, color: color?.value })}
                      className={`flex-1 p-3 flex flex-col items-center space-y-2 border-2 rounded-xl transition-all ${
                        formData?.color === color?.value
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full ${color?.class}`} />
                      <span className="text-xs text-muted-foreground">{color?.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Invite Participants */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Invitation Type Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  ¿Cómo quieres invitar?
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setFormData({ ...formData, invitationType: 'squad' })}
                    className={`p-4 flex flex-col items-center space-y-2 border-2 rounded-xl transition-all ${
                      formData?.invitationType === 'squad' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name="Users" size={24} className="text-foreground" />
                    <span className="text-sm font-medium text-foreground">Squad</span>
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, invitationType: 'username' })}
                    className={`p-4 flex flex-col items-center space-y-2 border-2 rounded-xl transition-all ${
                      formData?.invitationType === 'username' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name="User" size={24} className="text-foreground" />
                    <span className="text-sm font-medium text-foreground">Usuario</span>
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, invitationType: 'email' })}
                    className={`p-4 flex flex-col items-center space-y-2 border-2 rounded-xl transition-all ${
                      formData?.invitationType === 'email' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name="Mail" size={24} className="text-foreground" />
                    <span className="text-sm font-medium text-foreground">Email</span>
                  </button>
                </div>
              </div>

              {/* Squad Selection */}
              {formData?.invitationType === 'squad' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Selecciona tu Squad
                  </label>
                  {squads?.length > 0 ? (
                    <div className="space-y-3">
                      {squads?.map((squad) => (
                        <button
                          key={squad?.id}
                          onClick={() => setFormData({ ...formData, selectedSquadId: squad?.id })}
                          className={`w-full p-4 flex items-center space-x-3 border-2 rounded-xl transition-all ${
                            formData?.selectedSquadId === squad?.id
                              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className={`w-12 h-12 rounded-full bg-${squad?.color}/10 flex items-center justify-center`}>
                            <Icon name={squad?.icon} size={24} className={`text-${squad?.color}`} />
                          </div>
                          <div className="flex-1 text-left">
                            <h4 className="font-semibold text-foreground">{squad?.name}</h4>
                            <p className="text-sm text-muted-foreground">{squad?.description}</p>
                          </div>
                          {formData?.selectedSquadId === squad?.id && (
                            <Icon name="CheckCircle" size={20} className="text-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center bg-muted/30 rounded-xl border border-border">
                      <Icon name="Users" size={32} className="text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No tienes squads aún</p>
                    </div>
                  )}
                </div>
              )}

              {/* Username Search */}
              {formData?.invitationType === 'username' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Buscar por nombre de usuario
                  </label>
                  <Input
                    value={searchTerm}
                    onChange={(e) => handleSearchUsers(e?.target?.value)}
                    placeholder="Buscar usuarios..."
                    iconName="Search"
                  />
                  
                  {searching && (
                    <div className="mt-3 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" />
                    </div>
                  )}

                  {searchResults?.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                      {searchResults?.map((user) => (
                        <button
                          key={user?.id}
                          onClick={() => toggleUserSelection(user?.id)}
                          className={`w-full p-3 flex items-center space-x-3 border-2 rounded-lg transition-all ${
                            formData?.selectedUserIds?.includes(user?.id)
                              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            {user?.avatar_url ? (
                              <img src={user?.avatar_url} alt={user?.full_name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <Icon name="User" size={20} className="text-primary" />
                            )}
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-medium text-foreground">{user?.full_name}</p>
                            <p className="text-xs text-muted-foreground">{user?.total_points} puntos</p>
                          </div>
                          {formData?.selectedUserIds?.includes(user?.id) && (
                            <Icon name="CheckCircle" size={20} className="text-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {searchTerm && !searching && searchResults?.length === 0 && (
                    <div className="mt-3 p-4 text-center bg-muted/30 rounded-lg border border-border">
                      <p className="text-sm text-muted-foreground">No se encontraron usuarios</p>
                    </div>
                  )}

                  {formData?.selectedUserIds?.length > 0 && (
                    <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                      <p className="text-sm font-medium text-foreground mb-2">
                        Usuarios seleccionados: {formData?.selectedUserIds?.length}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Email Invitation */}
              {formData?.invitationType === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Invitar por correo electrónico
                  </label>
                  <Input
                    type="email"
                    value={formData?.inviteEmail}
                    onChange={(e) => setFormData({ ...formData, inviteEmail: e?.target?.value })}
                    placeholder="usuario@ejemplo.com"
                    iconName="Mail"
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    Se enviará una invitación al correo especificado
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex space-x-3 p-6 border-t border-border/40">
          {step === 2 && (
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              iconName="ChevronLeft"
            >
              Atrás
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleClose}
            fullWidth={step === 1}
          >
            Cancelar
          </Button>
          {step === 1 ? (
            <Button
              onClick={() => setStep(2)}
              disabled={!formData?.title?.trim() || !formData?.targetValue}
              fullWidth
              iconName="ChevronRight"
            >
              Siguiente
            </Button>
          ) : (
            <Button
              onClick={handleCreateChallenge}
              disabled={loading || (
                formData?.invitationType === 'squad' && !formData?.selectedSquadId ||
                formData?.invitationType === 'username' && formData?.selectedUserIds?.length === 0 ||
                formData?.invitationType === 'email' && !formData?.inviteEmail?.trim()
              )}
              loading={loading}
              fullWidth
              iconName="Plus"
            >
              Crear Reto
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CreateChallengeModal;